import type { QueryClient } from '@tanstack/vue-query'
import type {
  NavigationGuardWithThis,
  RouteLocationNormalized,
  RouteLocationRaw,
  RouteRecordNormalized,
} from 'vue-router'

/** What a gate may read besides the session store: the app's query cache, for an answer it asks once. */
export interface GateContext {
  queryClient: QueryClient
}

/**
 * What a route asks of the organization its URL names beyond the permissions `meta.requires` lists:
 * a public flag on it, its platform selling credits, its export log holding a row.
 *
 * `true` opens the route. `false` refuses it, and the navigation lands on that organization's home,
 * as a refused permission does. A location sends it there instead, and names the organization:
 * vue-router resolves a location's missing params against the page being left, which during a
 * change of organization is the other one's.
 */
export type OrganizationGate = (
  organizationId: string,
  context: GateContext,
) => boolean | RouteLocationRaw | Promise<boolean | RouteLocationRaw>

declare module 'vue-router' {
  interface RouteMeta {
    /** Asked by the organization gate guard ({@link createOrganizationGateGuard}). */
    gate?: OrganizationGate
  }
}

function organizationOf(location: RouteLocationNormalized): string | undefined {
  const id = location.params.organizationId
  return typeof id === 'string' && id !== '' ? id : undefined
}

/** vue-router's own test of whether a navigation keeps a record: an alias is its original. */
function sameRecord(a: RouteRecordNormalized, b: RouteRecordNormalized): boolean {
  return (a.aliasOf ?? a) === (b.aliasOf ?? b)
}

/**
 * The gates a navigation asks, outermost record first: the gate of every record it enters and, when
 * the organization in the URL changes, the gate of every record it keeps as well — the same page of
 * another organization reuses the route, so vue-router counts it as staying and runs no `beforeEnter`
 * there, while the answer belongs to the organization. A change of any other param or of the query
 * asks nothing again: a gate reads the organization alone.
 */
export function gatesToAsk(to: RouteLocationNormalized, from: RouteLocationNormalized): OrganizationGate[] {
  const otherOrganization = organizationOf(to) !== organizationOf(from)
  const gates: OrganizationGate[] = []
  for (const record of to.matched) {
    const gate = record.meta.gate
    if (gate === undefined) continue
    if (otherOrganization || !from.matched.some((kept) => sameRecord(kept, record))) gates.push(gate)
  }
  return gates
}

/**
 * The one place a route's {@link OrganizationGate} is asked — never a `beforeEnter`, which would miss
 * the change of organization above.
 *
 * **Registered after the session guard and the permission guard.** The session guard has set
 * `X-Org-Id` to the organization being entered and read its `GET /access`, so a gate reading the
 * session store reads that organization's answer; the permission guard has refused what the caller
 * may not do there, so a gate that asks the server something is only asked where the screen is the
 * caller's to see. A gate is asked with the organization of the URL being entered, never the one
 * the page shows.
 */
export function createOrganizationGateGuard(context: GateContext): NavigationGuardWithThis<undefined> {
  return async (to, from) => {
    const organizationId = organizationOf(to)
    if (organizationId === undefined) return true
    for (const gate of gatesToAsk(to, from)) {
      const answer = await gate(organizationId, context)
      if (answer === false) return { name: 'home', params: { organizationId } }
      if (answer !== true) return answer
    }
    return true
  }
}
